import type { AhPayload } from './ahReport';

/*
 * Mock AH campaign payload — ported from the design handoff's data.js v2
 * (seeded, deterministic). Used as a render fallback ("Voorbeelddata") until
 * the live AH report is reachable. `days` are ISO strings to match the API
 * contract; useAhReport prefers live data over this.
 */

const CAMPAIGN_DAYS = 28;
const DAYS: string[] = [];
{
  const start = new Date(Date.UTC(2026, 5, 8)); // 8 June 2026 (go-live)
  for (let i = 0; i < CAMPAIGN_DAYS; i++) {
    const d = new Date(start);
    d.setUTCDate(d.getUTCDate() + i);
    DAYS.push(d.toISOString());
  }
}

function seeded(seed: number) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

function trend(total: number, seed: number): number[] {
  const rand = seeded(seed);
  const n = DAYS.length;
  const raw: number[] = [];
  for (let i = 0; i < n; i++) {
    const launchBoost = Math.exp(-i / 4) * 1.6;
    const dow = (i + 1) % 7;
    const weekend = dow === 5 || dow === 6 ? 1.18 : 1;
    const p2Lift = i >= 14 ? 1.08 : 1; // P2 lift (silver swap, fresh content)
    const decay = 1 - i / (n * 3);
    const noise = 0.85 + rand() * 0.3;
    raw.push((1 + launchBoost) * weekend * decay * noise * p2Lift);
  }
  const sum = raw.reduce((a, b) => a + b, 0);
  return raw.map((v) => Math.round((v / sum) * total));
}

const totals: AhPayload['totals'] = {
  registrations: 312840,
  uniqueVisitors: 521430,
  uniquePlayers: 418660,
  gameplays: 3284920,
  missions: 642180,
  activeUsers: 28140,
  avgSessions: 11.42,
  avgGamesPerUser: 9.8,
  activeDaysPerUser: 6.84,
  presentationTime: '84,210 hr',
  avgPresentationPerUser: '9:42',
  avgSessionDuration: '6m 20s',
  couponsIssued: 218400,
  couponsRedeemed: 89240,
  bothGoldAndSilver: 38.6,
  uniqueVisits: 1284600,
  practiceGames: 1842300,
};

const venues: AhPayload['venues'] = [
  // ---- Premium / headline (own engagement model — no gameplay) ----
  {
    id: 'ah',
    name: 'Albert Heijn',
    tier: 'premium',
    period: 'both',
    color: '#00ADE6',
    game: null,
    visits: 412840,
    uniqueUsers: 286400,
    avgTime: '3:28',
    totalTime: '23,840 hr',
    details: { 'Content views': 198420, "Video's": 124302, Feedback: 8420 },
  },
  {
    id: 'knvb',
    name: 'KNVB',
    tier: 'premium',
    period: 'both',
    color: '#FF6B1A',
    game: null,
    visits: 328420,
    uniqueUsers: 231050,
    avgTime: '4:02',
    totalTime: '22,140 hr',
    details: { 'Content views': 164900, 'Foto-filters gemaakt': 152450, Feedback: 41280 },
  },

  // ---- Gold (own game, both periods) ----
  {
    id: 'heineken',
    name: 'Heineken',
    tier: 'gold',
    period: 'both',
    color: '#0F8A4F',
    game: 'Quiz',
    visits: 384210,
    uniqueUsers: 264000,
    uniquePlayers: 198400,
    optins: 42100,
    gamesPlayed: 312840,
    practiceStarts: 142890,
    completionRate: 61,
    digitalMissions: 58420,
    txMissions: 18920,
    leaderboard: 14820,
    vouchersGiven: 96200,
    vouchersRedeemed: 41300,
    avgTime: '2:48',
    totalTime: '17,920 hr',
  },
  {
    id: 'snickers',
    name: 'Snickers',
    tier: 'gold',
    period: 'both',
    color: '#5E2E14',
    game: 'Fruit Ninja',
    visits: 312480,
    uniqueUsers: 221000,
    uniquePlayers: 168200,
    optins: 36400,
    gamesPlayed: 284290,
    practiceStarts: 128140,
    completionRate: 57,
    digitalMissions: 48120,
    txMissions: 14820,
    leaderboard: 11240,
    vouchersGiven: 84100,
    vouchersRedeemed: 33900,
    avgTime: '3:14',
    totalTime: '16,820 hr',
  },
  {
    id: 'lays',
    name: "Lay's",
    tier: 'gold',
    period: 'both',
    color: '#E8B53C',
    game: 'Bejeweled',
    visits: 298140,
    uniqueUsers: 212000,
    uniquePlayers: 162400,
    optins: 38900,
    gamesPlayed: 271240,
    practiceStarts: 121280,
    completionRate: 63,
    digitalMissions: 52840,
    txMissions: 16420,
    leaderboard: 12820,
    vouchersGiven: 88300,
    vouchersRedeemed: 38100,
    avgTime: '3:42',
    totalTime: '18,420 hr',
  },
  {
    id: 'cocacola',
    name: 'Coca-Cola',
    tier: 'gold',
    period: 'both',
    color: '#D11A2F',
    game: 'Basket Runner',
    visits: 342180,
    uniqueUsers: 238000,
    uniquePlayers: 181000,
    optins: 40200,
    gamesPlayed: 298410,
    practiceStarts: 132820,
    completionRate: 59,
    digitalMissions: 54820,
    txMissions: 17240,
    leaderboard: 13420,
    vouchersGiven: 91500,
    vouchersRedeemed: 37800,
    avgTime: '3:01',
    totalTime: '17,180 hr',
  },
  {
    id: 'bud',
    name: 'BUD',
    tier: 'gold',
    period: 'both',
    color: '#1A2B5E',
    game: 'Guitar Hero',
    visits: 218400,
    uniqueUsers: 154000,
    uniquePlayers: 118200,
    optins: 24800,
    gamesPlayed: 184290,
    practiceStarts: 84290,
    completionRate: 55,
    digitalMissions: 38120,
    txMissions: 11820,
    leaderboard: 8420,
    vouchersGiven: 58200,
    vouchersRedeemed: 21900,
    avgTime: '2:54',
    totalTime: '10,580 hr',
  },

  // ---- Silver (Penalty Shooter, free games, digital missions) ----
  {
    id: 'heinz',
    name: 'Heinz',
    tier: 'silver',
    period: 1,
    color: '#E63946',
    game: 'Penalty Shooter',
    visits: 78420,
    uniqueUsers: 56200,
    uniquePlayers: 42100,
    practiceStarts: 60909,
    gamesPlayed: 132410,
    completionRate: 49,
    digitalMissions: 24820,
    vouchersGiven: 36200,
    vouchersRedeemed: 13000,
    avgTime: '1:42',
    totalTime: '2,220 hr',
  },
  {
    id: 'droetker',
    name: 'Dr. Oetker',
    tier: 'silver',
    period: 1,
    color: '#D4002A',
    game: 'Penalty Shooter',
    visits: 71280,
    uniqueUsers: 51000,
    uniquePlayers: 38200,
    practiceStarts: 56046,
    gamesPlayed: 121840,
    completionRate: 48,
    digitalMissions: 21840,
    vouchersGiven: 33800,
    vouchersRedeemed: 11500,
    avgTime: '1:38',
    totalTime: '1,940 hr',
  },
  {
    id: 'page',
    name: 'Page',
    tier: 'silver',
    period: 1,
    color: '#7BA9D5',
    game: 'Penalty Shooter',
    visits: 58210,
    uniqueUsers: 42000,
    uniquePlayers: 31100,
    practiceStarts: 45273,
    gamesPlayed: 98420,
    completionRate: 46,
    digitalMissions: 18420,
    vouchersGiven: 27600,
    vouchersRedeemed: 8600,
    avgTime: '1:24',
    totalTime: '1,360 hr',
  },
  {
    id: 'starbucks',
    name: 'Starbucks',
    tier: 'silver',
    period: 1,
    color: '#00704A',
    game: 'Penalty Shooter',
    visits: 92180,
    uniqueUsers: 65200,
    uniquePlayers: 49100,
    practiceStarts: 72873,
    gamesPlayed: 158420,
    completionRate: 53,
    digitalMissions: 32140,
    vouchersGiven: 42800,
    vouchersRedeemed: 18000,
    avgTime: '1:54',
    totalTime: '2,920 hr',
  },
  {
    id: 'tuc',
    name: 'TUC',
    tier: 'silver',
    period: 2,
    color: '#F0B400',
    game: 'Penalty Shooter',
    visits: 72180,
    uniqueUsers: 52000,
    uniquePlayers: 39000,
    practiceStarts: 56046,
    gamesPlayed: 121840,
    completionRate: 47,
    digitalMissions: 22140,
    vouchersGiven: 34200,
    vouchersRedeemed: 11300,
    avgTime: '1:36',
    totalTime: '1,920 hr',
  },
  {
    id: 'lookolook',
    name: 'Look-O-Look',
    tier: 'silver',
    period: 2,
    color: '#E91E63',
    game: 'Penalty Shooter',
    visits: 68420,
    uniqueUsers: 49000,
    uniquePlayers: 36200,
    practiceStarts: 54377,
    gamesPlayed: 118210,
    completionRate: 46,
    digitalMissions: 20420,
    vouchersGiven: 32600,
    vouchersRedeemed: 10400,
    avgTime: '1:32',
    totalTime: '1,740 hr',
  },
  {
    id: 'felix',
    name: 'Felix',
    tier: 'silver',
    period: 2,
    color: '#E84A1A',
    game: 'Penalty Shooter',
    visits: 81240,
    uniqueUsers: 58000,
    uniquePlayers: 44000,
    practiceStarts: 63673,
    gamesPlayed: 138420,
    completionRate: 50,
    digitalMissions: 26420,
    vouchersGiven: 38400,
    vouchersRedeemed: 14200,
    avgTime: '1:48',
    totalTime: '2,440 hr',
  },

  // ---- Bronze (branding / sponsorship only — no game) ----
  {
    id: 'ariel',
    name: 'Ariel',
    tier: 'bronze',
    period: 1,
    color: '#1B6FB8',
    game: null,
    visits: 84210,
    uniqueUsers: 60100,
    avgTime: '0:52',
    totalTime: '1,210 hr',
    details: { 'Logo impressies': 412000, 'Click-throughs': 14200 },
  },
  {
    id: 'robijn',
    name: 'Robijn',
    tier: 'bronze',
    period: 2,
    color: '#9B5DE5',
    game: null,
    visits: 64820,
    uniqueUsers: 46300,
    avgTime: '0:46',
    totalTime: '820 hr',
    details: { 'Logo impressies': 384000, 'Click-throughs': 11800 },
  },
  {
    id: 'nlo',
    name: 'NLO',
    tier: 'bronze',
    period: 'both',
    color: '#5A6B85',
    game: null,
    visits: 28420,
    uniqueUsers: 21000,
    avgTime: '0:48',
    totalTime: '380 hr',
    details: { 'Logo impressies': 412840, 'Click-throughs': 12420 },
  },
  {
    id: 'oldamsterdam',
    name: 'Old Amsterdam',
    tier: 'bronze',
    period: 'w25',
    color: '#B89030',
    game: null,
    visits: 24180,
    uniqueUsers: 18000,
    avgTime: '0:42',
    totalTime: '284 hr',
    details: { 'Logo impressies': 384210, 'Click-throughs': 10820 },
  },
];

export const AH_FIXTURE: AhPayload = {
  days: DAYS,
  registrations: trend(totals.registrations, 11),
  uniqueVisitors: trend(totals.uniqueVisitors, 22),
  gameplays: trend(totals.gameplays, 33),
  missions: trend(totals.missions, 44),
  couponsRedeemed: trend(totals.couponsRedeemed, 66),
  couponsByDay: {
    days: DAYS,
    issued: trend(totals.couponsIssued, 55),
    redeemed: trend(totals.couponsRedeemed, 66),
  },
  venues,
  venueOverlap: [
    { label: '1 venue', pct: 24.2 },
    { label: '2 venues', pct: 21.8 },
    { label: '3 venues', pct: 18.4 },
    { label: '4 venues', pct: 14.1 },
    { label: '5+ venues', pct: 21.5 },
  ],
  crossVenue: { goldAndSilverPct: 38.6, avgVenuesPerUser: 2.9 },
  menuOpens: {
    total: 124820,
    breakdown: [
      { label: 'Hoe werkt het', count: 52840 },
      { label: 'Veelgestelde vragen', count: 34210 },
      { label: 'Mijn gewonnen prijzen', count: 22480 },
      { label: 'Voorwaarden', count: 15290 },
    ],
  },
  totals,
  periods: {
    p1: { label: 'Periode 1', start: '8 jun', end: '21 jun', days: [0, 13] },
    p2: { label: 'Periode 2', start: '22 jun', end: '5 jul', days: [14, 27] },
  },
  campaign: {
    name: 'WC Albert Heijn',
    start: '8 jun 2026',
    end: '5 jul 2026',
    lastUpdated: '32 min geleden',
    currentDay: 18,
    totalDays: 28,
    currentPeriod: 2,
  },
};

/* ---------- Empty payload (shown when the live report is missing) ---------- */
// Same structure as the real report but ALL ZERO — so a missing/erroring
// backend renders the dashboard with 0s instead of the example data above.
function zeroVenue(v: AhPayload['venues'][number]): AhPayload['venues'][number] {
  const z = (n: number | null | undefined) => (n == null ? n : 0); // keep null (n/a), zero the rest
  return {
    ...v,
    visits: 0,
    avgTime: '0:00',
    totalTime: '0',
    uniqueUsers: z(v.uniqueUsers),
    uniquePlayers: z(v.uniquePlayers),
    optins: z(v.optins),
    gamesPlayed: z(v.gamesPlayed),
    practiceStarts: z(v.practiceStarts),
    completionRate: z(v.completionRate),
    digitalMissions: z(v.digitalMissions),
    txMissions: z(v.txMissions),
    leaderboard: z(v.leaderboard),
    vouchersGiven: z(v.vouchersGiven),
    vouchersRedeemed: z(v.vouchersRedeemed),
    details: v.details ? Object.fromEntries(Object.keys(v.details).map((k) => [k, 0])) : v.details,
  };
}

const ZEROS = DAYS.map(() => 0);

export const AH_EMPTY: AhPayload = {
  ...AH_FIXTURE,
  // Keep the day axis so the charts render a flat-zero line (not a blank/NaN chart).
  days: DAYS,
  registrations: ZEROS,
  uniqueVisitors: ZEROS,
  gameplays: ZEROS,
  missions: ZEROS,
  couponsRedeemed: ZEROS,
  couponsByDay: { days: DAYS, issued: ZEROS, redeemed: ZEROS },
  venues: venues.map(zeroVenue),
  venueOverlap: AH_FIXTURE.venueOverlap.map((o) => ({ ...o, pct: 0 })),
  crossVenue: { goldAndSilverPct: 0, avgVenuesPerUser: 0 },
  menuOpens: { total: 0, breakdown: AH_FIXTURE.menuOpens.breakdown.map((b) => ({ ...b, count: 0 })) },
  totals: {
    registrations: 0,
    uniqueVisitors: 0,
    uniquePlayers: 0,
    gameplays: 0,
    missions: 0,
    activeUsers: 0,
    avgSessions: 0,
    avgGamesPerUser: 0,
    activeDaysPerUser: 0,
    presentationTime: '0',
    avgPresentationPerUser: '0',
    avgSessionDuration: '0',
    couponsIssued: 0,
    couponsRedeemed: 0,
    bothGoldAndSilver: 0,
    uniqueVisits: 0,
    practiceGames: 0,
  },
  // campaign + periods kept from AH_FIXTURE (config, not data).
};
